import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import Chart from 'react-apexcharts';
//import { useDispatch, useSelector } from 'react-redux';
//import { setToogleHeader } from '../../core/redux/action';
import {
  TrendingUp,
  ShoppingBag,
  Users,
  Package,
  RefreshCw,
  FileText,
  CheckCircle,
  Clock,
  ArrowUp,
  ArrowDown,
  DollarSign,
  PieChart,
  Award,
  Star,
  Phone,
  Mail,
  User,
  Gift,
  Percent,
  Plus,
} from 'feather-icons-react/build/IconComponents';
import { salesDashboardActions } from '../../core/action/salesDashboardActions';
//import { all_routes } from '../../Router/all_routes';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const MySwal = withReactContent(Swal);

const SalesDashboard = () => {
  //const route = all_routes; // Commented out as it's not used
  // const dispatch = useDispatch();
  // const data = useSelector((state) => state.toggle_header);
  
  // State
  const [dashboardData, setDashboardData] = useState({
    salesPerson: {
      id: 1,
      name: 'Loading...',
      email: '',
      phone: '',
      avatar: '',
      join_date: ''
    },
    kpi: {
      totalSales: 0,
      salesGrowth: 0,
      totalOrders: 0,
      ordersGrowth: 0,
      totalCustomers: 0,
      customersGrowth: 0,
      conversionRate: 0,
      conversionGrowth: 0
    },
    targets: {
      monthlyTarget: 0,
      monthlyAchieved: 0,
      monthlyRemaining: 0,
      targetPercentage: 0,
      dailyAverage: 0,
      dailyNeeded: 0
    },
    commissions: {
      totalCommission: 0,
      commissionRate: 5,
      paidCommission: 0,
      pendingCommission: 0
    },
    performance: {
      avgOrderValue: 0,
      avgOrderGrowth: 0,
      customerSatisfaction: 0,
      satisfactionGrowth: 0
    },
    charts: {
      monthlySales: [],
      monthlyTargets: [],
      salesByCategory: []
    },
    activities: [],
    topProducts: [],
    topCustomers: [],
    tasks: [],
    pipeline: {
      prospecting: 0,
      qualification: 0,
      proposal: 0,
      negotiation: 0,
      closed: 0
    },
    badges: []
  });
  
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [salesPersonId] = useState(1); // This would come from auth (uncommented)

  // Load dashboard data from API
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      const result = await salesDashboardActions.getDashboardData({
        sales_person_id: salesPersonId,
        year: selectedYear,
        month: selectedMonth
      });
      
      if (result.success && result.data) {
        setDashboardData(result.data);
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: result.message || 'Failed to load dashboard data',
          timer: 3000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error loading sales dashboard:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load dashboard data',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  }, [salesPersonId, selectedYear, selectedMonth]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Format currency
  const formatCurrency = (amount) => {
    return salesDashboardActions.formatCurrency(amount);
  };

  // Format compact number
  const formatCompactNumber = (num) => {
    return salesDashboardActions.formatCompactNumber(num);
  };

  // Chart options for Sales vs Target
  const getSalesTargetChartOptions = () => {
    const salesData = dashboardData.charts?.monthlySales?.length ? dashboardData.charts.monthlySales : Array(12).fill(0);
    const targetData = dashboardData.charts?.monthlyTargets?.length ? dashboardData.charts.monthlyTargets : Array(12).fill(5000000);
    
    return {
      series: [
        {
          name: 'Actual Sales',
          data: salesData
        },
        {
          name: 'Monthly Target',
          data: targetData
        }
      ],
      colors: ['#28C76F', '#FF9F43'],
      chart: {
        type: 'bar',
        height: 300,
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4
        }
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        labels: {
          style: {
            colors: '#9aa4af',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (value) => formatCompactNumber(value),
          style: {
            colors: '#9aa4af',
            fontSize: '12px'
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: (value) => formatCurrency(value)
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '13px',
        markers: {
          width: 12,
          height: 12,
          radius: 6
        }
      },
      grid: {
        borderColor: '#f0f2f5',
        strokeDashArray: 5
      }
    };
  };

  // Sales by Category Pie Chart
  const getCategoryChartOptions = () => {
    const categoryData = dashboardData.charts?.salesByCategory?.length ? 
      dashboardData.charts.salesByCategory : 
      [{ category: 'No Data', amount: 1, percentage: 100 }];
    
    const seriesData = categoryData.map(item => item.amount || 0);
    const labelsData = categoryData.map(item => item.category || 'Other');
    const colors = ['#28C76F', '#FF9F43', '#00CFE8', '#EA5455', '#7367F0'];
    
    return {
      series: seriesData,
      chart: {
        type: 'donut',
        height: 280
      },
      labels: labelsData,
      colors: colors,
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => formatCompactNumber(dashboardData.kpi?.totalSales || 0)
              }
            }
          }
        }
      },
      dataLabels: { enabled: false },
      legend: {
        position: 'bottom',
        fontSize: '12px',
        markers: {
          width: 10,
          height: 10,
          radius: 5
        }
      },
      tooltip: {
        y: {
          formatter: (value) => formatCurrency(value)
        }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 200
          }
        }
      }]
    };
  };

  // Performance Gauge Chart
  const getGaugeChartOptions = () => {
    const percentage = dashboardData.targets?.targetPercentage || 0;
    
    return {
      series: [percentage],
      chart: {
        type: 'radialBar',
        height: 250,
        offsetY: -20
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: {
            margin: 0,
            size: '70%',
            background: '#fff'
          },
          track: {
            background: '#f0f2f5',
            strokeWidth: '67%',
            margin: 0
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: -20,
              show: true,
              color: '#9aa4af',
              fontSize: '14px'
            },
            value: {
              formatter: function(val) {
                return parseInt(val) + '%';
              },
              color: '#1B2850',
              fontSize: '24px',
              show: true,
              offsetY: 5
            }
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: ['#28C76F'],
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      stroke: {
        lineCap: 'round'
      },
      labels: ['Target Achieved']
    };
  };

  // Handle refresh
  const handleRefresh = () => {
    loadDashboardData();
  };

  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  // Handle month change
  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  // Handle add task
  const handleAddTask = async () => {
    const { value: formValues } = await MySwal.fire({
      title: 'Add New Task',
      html: `
        <input id="swal-task" class="swal2-input" placeholder="Task description">
        <input id="swal-date" class="swal2-input" type="date" placeholder="Due date">
        <select id="swal-priority" class="swal2-input">
          <option value="high">High Priority</option>
          <option value="medium" selected>Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Add Task',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        return {
          task: document.getElementById('swal-task').value,
          due_date: document.getElementById('swal-date').value,
          priority: document.getElementById('swal-priority').value
        };
      }
    });

    if (formValues) {
      const taskData = {
        sales_person_id: salesPersonId,
        task: formValues.task,
        due_date: formValues.due_date,
        due_time: '09:00 AM',
        priority: formValues.priority
      };

      const result = await salesDashboardActions.addTask(taskData);
      if (result.success) {
        MySwal.fire('Success', 'Task added successfully', 'success');
        loadDashboardData();
      } else {
        MySwal.fire('Error', result.message, 'error');
      }
    }
  };

  // Handle update task status
  const handleUpdateTaskStatus = async (taskId, status) => {
    const result = await salesDashboardActions.updateTaskStatus(taskId, status);
    if (result.success) {
      loadDashboardData();
    }
  };

  // Get priority badge class
  const getPriorityBadge = (priority) => {
    return salesDashboardActions.getPriorityBadge(priority);
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    return salesDashboardActions.getStatusBadge(status);
  };

  // Get time ago string
  const getTimeAgo = (time) => {
    if (!time) return '';
    const date = new Date(time);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading && !dashboardData.salesPerson.name) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="text-muted">Loading Sales Dashboard...</h5>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header with Sales Person Info */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="avatar avatar-xl bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center">
              {dashboardData.salesPerson.avatar ? (
                <img src={dashboardData.salesPerson.avatar} alt="Avatar" className="rounded-circle w-100 h-100" />
              ) : (
                <User size={40} className="text-primary" />
              )}
            </div>
            <div>
              <h2 className="mb-1">Welcome back, {dashboardData.salesPerson.name}!</h2>
              <div className="d-flex gap-3 text-muted flex-wrap">
                <span><Mail size={14} className="me-1" /> {dashboardData.salesPerson.email || 'email@company.com'}</span>
                <span><Phone size={14} className="me-1" /> {dashboardData.salesPerson.phone || '+91 98765 43210'}</span>
                <span><Award size={14} className="me-1" /> ID: {dashboardData.salesPerson.id}</span>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2">
            <div className="d-flex gap-2">
              <select 
                className="form-select form-select-sm" 
                style={{ width: '100px' }}
                value={selectedMonth}
                onChange={handleMonthChange}
              >
                <option value="1">Jan</option>
                <option value="2">Feb</option>
                <option value="3">Mar</option>
                <option value="4">Apr</option>
                <option value="5">May</option>
                <option value="6">Jun</option>
                <option value="7">Jul</option>
                <option value="8">Aug</option>
                <option value="9">Sep</option>
                <option value="10">Oct</option>
                <option value="11">Nov</option>
                <option value="12">Dec</option>
              </select>
              <select 
                className="form-select form-select-sm" 
                style={{ width: '100px' }}
                value={selectedYear}
                onChange={handleYearChange}
              >
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
                <option value={2024}>2024</option>
              </select>
            </div>
            <button className="btn btn-outline-light d-flex align-items-center gap-2" onClick={handleRefresh}>
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Performance Badges */}
        {dashboardData.badges && dashboardData.badges.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card bg-light">
                <div className="card-body py-2">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <span className="text-muted me-2">Your Achievements:</span>
                    {dashboardData.badges.map((badge, index) => (
                      <span key={index} className={`badge bg-${badge.color} bg-opacity-10 text-${badge.color} px-3 py-2 rounded-pill d-flex align-items-center gap-1`}>
                        {badge.icon === 'Award' && <Award size={14} />}
                        {badge.icon === 'Star' && <Star size={14} />}
                        {badge.icon === 'Users' && <Users size={14} />}
                        {badge.icon === 'TrendingUp' && <TrendingUp size={14} />}
                        {badge.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* KPI Cards Row 1 - Performance Metrics */}
        <div className="row g-3 mb-4">
          {/* Total Sales */}
          <div className="col-xl-3 col-sm-6">
            <div className="card bg-gradient-primary border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-white-50 mb-1">Total Sales</p>
                    <h3 className="text-white mb-0">
                      <CountUp
                        start={0}
                        end={dashboardData.kpi?.totalSales || 0}
                        duration={2}
                        formattingFn={formatCurrency}
                      />
                    </h3>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded p-2">
                    <DollarSign size={24} className="text-white" />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span className={`badge bg-white ${(dashboardData.kpi?.salesGrowth || 0) >= 0 ? 'text-success' : 'text-danger'} fw-semibold me-2`}>
                    {(dashboardData.kpi?.salesGrowth || 0) >= 0 ? <ArrowUp size={14} className="me-1" /> : <ArrowDown size={14} className="me-1" />}
                    {Math.abs(dashboardData.kpi?.salesGrowth || 0)}%
                  </span>
                  <span className="text-white-50 small">vs last month</span>
                </div>
              </div>
            </div>
          </div>

          {/* Total Orders */}
          <div className="col-xl-3 col-sm-6">
            <div className="card bg-gradient-success border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-white-50 mb-1">Total Orders</p>
                    <h3 className="text-white mb-0">
                      <CountUp
                        start={0}
                        end={dashboardData.kpi?.totalOrders || 0}
                        duration={2}
                      />
                    </h3>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded p-2">
                    <ShoppingBag size={24} className="text-white" />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span className={`badge bg-white ${(dashboardData.kpi?.ordersGrowth || 0) >= 0 ? 'text-success' : 'text-danger'} fw-semibold me-2`}>
                    {(dashboardData.kpi?.ordersGrowth || 0) >= 0 ? <ArrowUp size={14} className="me-1" /> : <ArrowDown size={14} className="me-1" />}
                    {Math.abs(dashboardData.kpi?.ordersGrowth || 0)}%
                  </span>
                  <span className="text-white-50 small">vs last month</span>
                </div>
              </div>
            </div>
          </div>

          {/* New Customers */}
          <div className="col-xl-3 col-sm-6">
            <div className="card bg-gradient-info border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-white-50 mb-1">New Customers</p>
                    <h3 className="text-white mb-0">
                      <CountUp
                        start={0}
                        end={dashboardData.kpi?.totalCustomers || 0}
                        duration={2}
                      />
                    </h3>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded p-2">
                    <Users size={24} className="text-white" />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span className={`badge bg-white ${(dashboardData.kpi?.customersGrowth || 0) >= 0 ? 'text-success' : 'text-danger'} fw-semibold me-2`}>
                    {(dashboardData.kpi?.customersGrowth || 0) >= 0 ? <ArrowUp size={14} className="me-1" /> : <ArrowDown size={14} className="me-1" />}
                    {Math.abs(dashboardData.kpi?.customersGrowth || 0)}%
                  </span>
                  <span className="text-white-50 small">vs last month</span>
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="col-xl-3 col-sm-6">
            <div className="card bg-gradient-warning border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="text-white-50 mb-1">Conversion Rate</p>
                    <h3 className="text-white mb-0">
                      <CountUp
                        start={0}
                        end={dashboardData.kpi?.conversionRate || 0}
                        duration={2}
                        suffix="%"
                      />
                    </h3>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded p-2">
                    <Percent size={24} className="text-white" />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span className={`badge bg-white ${(dashboardData.kpi?.conversionGrowth || 0) >= 0 ? 'text-success' : 'text-danger'} fw-semibold me-2`}>
                    {(dashboardData.kpi?.conversionGrowth || 0) >= 0 ? <ArrowUp size={14} className="me-1" /> : <ArrowDown size={14} className="me-1" />}
                    {Math.abs(dashboardData.kpi?.conversionGrowth || 0)}%
                  </span>
                  <span className="text-white-50 small">vs last month</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 - Target Progress & Commission */}
        <div className="row g-3 mb-4">
          {/* Target Progress Card */}
          <div className="col-xl-4 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Monthly Target Progress</h5>
                <span className="badge bg-primary">
                  Target: {formatCurrency(dashboardData.targets?.monthlyTarget || 0)}
                </span>
              </div>
              <div className="card-body">
                <Chart
                  options={getGaugeChartOptions()}
                  series={getGaugeChartOptions().series}
                  type="radialBar"
                  height={200}
                />
                
                <div className="text-center mt-2">
                  <h3 className="text-success mb-0">{formatCurrency(dashboardData.targets?.monthlyAchieved || 0)}</h3>
                  <p className="text-muted">Achieved</p>
                  <h5 className="text-warning mb-0">{formatCurrency(dashboardData.targets?.monthlyRemaining || 0)}</h5>
                  <p className="text-muted">Remaining</p>
                </div>

                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Daily Average</span>
                    <span className="fw-bold">{formatCurrency(dashboardData.targets?.dailyAverage || 0)}</span>
                  </div>
                  <div className="progress mb-2" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-success" 
                      style={{ width: `${Math.min(100, (dashboardData.targets?.dailyAverage || 0) / ((dashboardData.targets?.dailyNeeded || 1) * 100))}%` }}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">To reach target</span>
                    <span className="fw-bold text-warning">{formatCurrency(dashboardData.targets?.dailyNeeded || 0)}/day</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Commission Card */}
          <div className="col-xl-4 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 pt-3">
                <h5 className="mb-0">Commission Summary</h5>
              </div>
              <div className="card-body">
                <div className="text-center mb-4">
                  <div className="bg-success bg-opacity-10 rounded-circle p-4 d-inline-flex mb-3">
                    <Gift size={48} className="text-success" />
                  </div>
                  <h2 className="mb-1">{formatCurrency(dashboardData.commissions?.totalCommission || 0)}</h2>
                  <p className="text-muted">Total Commission ({dashboardData.commissions?.commissionRate || 5}% of sales)</p>
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <div className="bg-light rounded p-3 text-center">
                      <CheckCircle size={24} className="text-success mb-2" />
                      <h5 className="mb-1">{formatCurrency(dashboardData.commissions?.paidCommission || 0)}</h5>
                      <small className="text-muted">Paid</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="bg-light rounded p-3 text-center">
                      <Clock size={24} className="text-warning mb-2" />
                      <h5 className="mb-1">{formatCurrency(dashboardData.commissions?.pendingCommission || 0)}</h5>
                      <small className="text-muted">Pending</small>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <Link to="/commissions" className="btn btn-outline-primary w-100">
                    View Commission Details
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="col-xl-4 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 pt-3">
                <h5 className="mb-0">Performance Metrics</h5>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <p className="text-muted mb-1">Avg. Order Value</p>
                    <h3 className="mb-0">{formatCurrency(dashboardData.performance?.avgOrderValue || 0)}</h3>
                    <small className={dashboardData.performance?.avgOrderGrowth >= 0 ? 'text-success' : 'text-danger'}>
                      {dashboardData.performance?.avgOrderGrowth >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                      {Math.abs(dashboardData.performance?.avgOrderGrowth || 0)}%
                    </small>
                  </div>
                  <div className="bg-primary bg-opacity-10 rounded p-3">
                    <ShoppingBag size={24} className="text-primary" />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <p className="text-muted mb-1">Customer Satisfaction</p>
                    <h3 className="mb-0">{dashboardData.performance?.customerSatisfaction || 0}/5.0</h3>
                    <small className="text-success">
                      <ArrowUp size={12} /> {dashboardData.performance?.satisfactionGrowth || 0}
                    </small>
                  </div>
                  <div className="bg-warning bg-opacity-10 rounded p-3">
                    <Star size={24} className="text-warning" />
                  </div>
                </div>

                <div className="mt-4">
                  <h6 className="mb-3">Pipeline Summary</h6>
                  <div className="row g-2">
                    <div className="col-4">
                      <div className="bg-light rounded p-2 text-center">
                        <small className="text-muted d-block">Prospecting</small>
                        <strong>{dashboardData.pipeline?.prospecting || 0}</strong>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="bg-light rounded p-2 text-center">
                        <small className="text-muted d-block">Qualification</small>
                        <strong>{dashboardData.pipeline?.qualification || 0}</strong>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="bg-light rounded p-2 text-center">
                        <small className="text-muted d-block">Proposal</small>
                        <strong>{dashboardData.pipeline?.proposal || 0}</strong>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-light rounded p-2 text-center">
                        <small className="text-muted d-block">Negotiation</small>
                        <strong>{dashboardData.pipeline?.negotiation || 0}</strong>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-light rounded p-2 text-center bg-success bg-opacity-10">
                        <small className="text-muted d-block">Closed</small>
                        <strong className="text-success">{dashboardData.pipeline?.closed || 0}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3 - Sales vs Target Chart & Category Distribution */}
        <div className="row g-3 mb-4">
          {/* Sales vs Target Chart */}
          <div className="col-xl-8 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Sales Performance vs Target</h5>
                <div className="d-flex gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-success" style={{ width: '12px', height: '12px', borderRadius: '50%' }}></span>
                    <small>Actual Sales</small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-warning" style={{ width: '12px', height: '12px', borderRadius: '50%' }}></span>
                    <small>Target</small>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <Chart
                  options={getSalesTargetChartOptions()}
                  series={getSalesTargetChartOptions().series}
                  type="bar"
                  height={280}
                />
              </div>
            </div>
          </div>

          {/* Sales by Category */}
          <div className="col-xl-4 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Sales by Category</h5>
                <PieChart size={18} className="text-muted" />
              </div>
              <div className="card-body">
                <Chart
                  options={getCategoryChartOptions()}
                  series={getCategoryChartOptions().series}
                  type="donut"
                  height={220}
                />
                
                <div className="mt-3">
                  {dashboardData.charts?.salesByCategory?.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge" style={{ 
                          width: '10px', 
                          height: '10px', 
                          borderRadius: '50%',
                          backgroundColor: getCategoryChartOptions().colors[index % getCategoryChartOptions().colors.length]
                        }}></span>
                        <small>{item.category}</small>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <small className="text-muted">{formatCompactNumber(item.amount)}</small>
                        <small className="fw-bold">{item.percentage || 0}%</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 4 - Top Products & Recent Activities */}
        <div className="row g-3 mb-4">
          {/* Top Selling Products */}
          <div className="col-xl-6 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Top Selling Products</h5>
                <Link to="/products" className="text-primary small">View All</Link>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Product</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Qty</th>
                        <th className="text-end">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.topProducts?.length > 0 ? (
                        dashboardData.topProducts.map((product, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm bg-light rounded me-2">
                                  <Package size={16} />
                                </div>
                                <div>
                                  <h6 className="mb-0 small">{product.name}</h6>
                                </div>
                              </div>
                            </td>
                            <td className="text-end">{formatCurrency(product.price)}</td>
                            <td className="text-end">{product.quantity}</td>
                            <td className="text-end fw-bold text-success">{formatCompactNumber(product.revenue)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-3">
                            No product data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="col-xl-6 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Recent Activities</h5>
                <Link to="/activities" className="text-primary small">View All</Link>
              </div>
              <div className="card-body">
                <div className="activities-timeline">
                  {dashboardData.activities?.length > 0 ? (
                    dashboardData.activities.map((activity, index) => (
                      <div key={index} className="d-flex align-items-start mb-3">
                        <div className="me-3">
                          <div className={`rounded-circle p-2 ${
                            activity.type === 'sale' ? 'bg-success bg-opacity-10' :
                            activity.type === 'call' ? 'bg-info bg-opacity-10' :
                            activity.type === 'meeting' ? 'bg-primary bg-opacity-10' :
                            activity.type === 'proposal' ? 'bg-warning bg-opacity-10' :
                            'bg-secondary bg-opacity-10'
                          }`}>
                            {activity.type === 'sale' && <ShoppingBag size={16} className="text-success" />}
                            {activity.type === 'call' && <Phone size={16} className="text-info" />}
                            {activity.type === 'meeting' && <Users size={16} className="text-primary" />}
                            {activity.type === 'proposal' && <FileText size={16} className="text-warning" />}
                            {activity.type === 'followup' && <Clock size={16} className="text-secondary" />}
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between">
                            <h6 className="mb-1">{activity.customer}</h6>
                            <small className="text-muted">{getTimeAgo(activity.time)}</small>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {activity.type === 'sale' && `Sale: ${formatCurrency(activity.amount)}`}
                              {activity.type === 'call' && `Call duration: ${activity.duration || '15 min'}`}
                              {activity.type === 'meeting' && `Meeting at ${activity.location || 'Office'}`}
                              {activity.type === 'proposal' && `Proposal: ${formatCurrency(activity.amount)}`}
                              {activity.type === 'followup' && 'Follow-up required'}
                            </small>
                            <span className={`badge ${getStatusBadge(activity.status)} bg-opacity-10 text-${activity.status === 'completed' ? 'success' : 'warning'} px-2`}>
                              {activity.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted py-3">
                      No recent activities
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 5 - Top Customers & Upcoming Tasks */}
        <div className="row g-3">
          {/* Top Customers */}
          <div className="col-xl-7 col-sm-12">
            <div className="card">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Top Customers</h5>
                <Link to="/customers" className="text-primary small">View All</Link>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Customer</th>
                        <th>Company</th>
                        <th className="text-end">Orders</th>
                        <th className="text-end">Total Spent</th>
                        <th>Last Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.topCustomers?.length > 0 ? (
                        dashboardData.topCustomers.map((customer, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar avatar-sm bg-primary bg-opacity-10 rounded-circle me-2 d-flex align-items-center justify-content-center">
                                  <Users size={14} className="text-primary" />
                                </div>
                                <div>
                                  <h6 className="mb-0 small">{customer.name}</h6>
                                </div>
                              </div>
                            </td>
                            <td>{customer.company || 'N/A'}</td>
                            <td className="text-end">{customer.orders}</td>
                            <td className="text-end fw-bold text-success">{formatCompactNumber(customer.total_spent || 0)}</td>
                            <td><small className="text-muted">{getTimeAgo(customer.last_order)}</small></td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-3">
                            No customer data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Upcoming Tasks */}
          <div className="col-xl-5 col-sm-12">
            <div className="card h-100">
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3">
                <h5 className="mb-0">Upcoming Tasks</h5>
                <span className="badge bg-primary">{dashboardData.tasks?.length || 0} Tasks</span>
              </div>
              <div className="card-body">
                <div className="list-group list-group-flush">
                  {dashboardData.tasks?.length > 0 ? (
                    dashboardData.tasks.map((task, index) => (
                      <div key={index} className="list-group-item px-0">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex gap-2">
                            <div className={`rounded-circle p-1 mt-1 ${getPriorityBadge(task.priority)} bg-opacity-10`}>
                              <Clock size={14} className={`text-${task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'}`} />
                            </div>
                            <div>
                              <h6 className="mb-1">{task.task}</h6>
                              <small className="text-muted">{task.time}</small>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <span className={`badge ${getPriorityBadge(task.priority)} bg-opacity-10 text-${task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'} px-2`}>
                              {task.priority}
                            </span>
                            <button 
                              className="btn btn-sm btn-outline-success p-0 px-2"
                              onClick={() => handleUpdateTaskStatus(task.id, 'completed')}
                              title="Mark as completed"
                            >
                              <CheckCircle size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted py-3">
                      No pending tasks
                    </div>
                  )}
                </div>
                
                <div className="mt-3">
                  <button className="btn btn-outline-primary w-100" onClick={handleAddTask}>
                    <Plus size={16} className="me-1" />
                    Add New Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;